import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EventOriginEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { Space, SpaceInput } from '@app/core/model/entities/asset/space';
import { Section } from '@app/core/model/other/field-config';
import { FormStateService } from '@app/shared/components/form-builder/form-state.service';
import { GeneratesObject } from '@app/shared/interfaces/generates-object';
import { TranslateService } from '@ngx-translate/core';
import { AccessManager } from '@services/managers/access.manager';
import { SectionService } from '@services/section.service';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Component({
  selector: 'organization-space-create-modal',
  templateUrl: './organization-space-create-modal.component.html',
  providers: [FormStateService],
  standalone: false
})
export class OrganizationSpaceCreateModalComponent implements OnInit, GeneratesObject {
  public formId = 'space_creation_dialog';
  protected eventsOrigin = EventOriginEnum.SHEET;
  public Permission = PermissionEnum;
  public permissionsForEdition = [PermissionEnum.CREATE_SPACE];
  protected missingRequiredFields$: Observable<{ code: string, label: string }[]>;
  protected form: Section[];

  protected data = inject<{ assetName?: string; parentSpace: Space; }>(MAT_DIALOG_DATA);

  private spaceInput: SpaceInput;

  protected formStateService = inject(FormStateService);
  private sectionService = inject(SectionService);
  protected destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private accessManager = inject(AccessManager);

  public ngOnInit(): void {
    const defaultMissingRequiredFields = [
      {code: 'assetId', label: this.translate.instant('LABEL.ASSET') as string},
      {code: 'parentPath', label: this.translate.instant('LABEL.PARENTPATH') as string},
    ].filter(({code}) => !this.accessManager.hasFieldWriteAccess(code, EntityTypeEnum.SPACE));

    this.missingRequiredFields$ = this.sectionService.getSectionsByFormCode(this.formId).pipe(
      tap(sections => this.form = sections),
      map(sections => sections.first()),
      map(section => {
        return this.sectionService.missingRequiredFields(section)
          .map(({code, label}) => ({code, label}))
          .concat(defaultMissingRequiredFields);
      }),
      takeUntilDestroyed(this.destroyRef)
    );

    this.formStateService.saved$
      .pipe(
        tap(({data}) => {
          Space.transformPropertiesForInput(data);
          this.spaceInput = data;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Get form data to use for creating a new Space.
   * @return Create Space form data.
   */
  public getGeneratedObject(): SpaceInput {
    return {
      ...this.spaceInput,
      assetId: this.data.parentSpace.assetId,
      parentPath: [...this.data.parentSpace.parentPath, this.data.parentSpace.id]
    };
  }
}
